import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import styled from "styled-components";

import { Button, LinkButton } from "../../../../components/Button";
import { MicroCircleContainer } from "../../../../components/CircleContainer/styles";
import { LivePreview } from "../../../../components/LivePreview";
import { useKeyPress } from "../../../../hooks/KeyPress";
import CloseIcon from "../../../../icons/Close";
import { Site } from "../../../../model/site";
import { stopScroll } from "../../../../utils/modal";

/* Slides in from the right, over the templates list (same as the mobile preview) */
const Overlay = styled.div<{ $open: boolean }>`
  position: fixed;
  inset: 0;
  z-index: 9998;
  display: flex;
  flex-direction: column;
  background: var(--color-neutral-darkness);
  box-shadow: -12px 0 32px rgba(0, 0, 0, 0.5);
  transform: translateX(${props => (props.$open ? "0" : "100%")});
  visibility: ${props => (props.$open ? "visible" : "hidden")};
  transition:
    transform 0.3s ease,
    visibility 0.3s;

  .header {
    display: flex;
    flex-shrink: 0;
    gap: 1rem;
    align-items: center;
    justify-content: space-between;
    padding: calc(12px + env(safe-area-inset-top)) 20px 12px;
    border-bottom: 1px solid var(--color-neutral-dark);

    h4 {
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    }
  }

  .body {
    display: flex;
    flex: 1;
    justify-content: center;
    min-height: 0;
  }

  /* Same layout as the screen's bottom bar: secondary action left, main one right */
  .footer {
    display: flex;
    flex-shrink: 0;
    flex-direction: column;
    gap: 0.5rem;
    padding: 1rem 20px calc(1rem + env(safe-area-inset-bottom));
    border-top: 1px solid var(--color-neutral-dark);

    .actions {
      display: flex;
      align-items: center;
      justify-content: space-between;
    }

    .error {
      font-family: Inter, sans-serif;
      font-size: 0.8125rem;
      color: var(--color-feedback-danger);
    }
  }
`;

type Props = {
  open: boolean;
  /** Name of the template shown (or being loaded). */
  name?: string;
  site: Site;
  loading: boolean;
  canUse: boolean;
  /** Saving the picked template. */
  saving?: boolean;
  error?: string;
  onClose: () => void;
  onUse: () => void;
};

/**
 * On screens without the side preview, picking a template opens it full screen right away, with the
 * actions to use it or go back to the list.
 */
export const MobileTemplatePreview = ({ open, name, site, loading, canUse, saving, error, onClose, onUse }: Props) => {
  const { t } = useTranslation();
  const prefix = "pages.editor.templates.mobilePreview";

  // Mounted on first open and then kept, so it can slide in and out
  const [mounted, setMounted] = useState(false);
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    if (open) {
      setMounted(true);
      // Next frame, so the slide-in runs after the overlay is in the DOM
      const frame = requestAnimationFrame(() => setVisible(true));
      return () => cancelAnimationFrame(frame);
    }
    setVisible(false);
  }, [open]);

  useEffect(() => (open ? stopScroll() : undefined), [open]);
  useKeyPress("Escape", () => open && onClose());

  if (!mounted) {
    return null;
  }

  return (
    <Overlay $open={visible} role="dialog" aria-label={name} aria-hidden={!visible}>
      <div className="header">
        <h4>{name}</h4>
        <MicroCircleContainer
          $color="var(--color-neutral-base)"
          cursor="pointer"
          aria-label={t(`${prefix}.close`)}
          onClick={onClose}>
          <CloseIcon height={9} width={9} color="var(--color-neutral-white)" />
        </MicroCircleContainer>
      </div>
      <div className="body">
        <LivePreview site={site} fullscreen loading={loading} />
      </div>
      <div className="footer">
        {error && <p className="error">{error}</p>}
        <div className="actions">
          <LinkButton onClick={onClose}>{t(`${prefix}.close`)}</LinkButton>
          <Button type="INFO" size="LG" disabled={!canUse} $loading={saving} onClick={() => canUse && onUse()}>
            {t(`${prefix}.use`)}
          </Button>
        </div>
      </div>
    </Overlay>
  );
};
